import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CreditCard, TrendingUp, Receipt, Zap, RefreshCw, Activity, Crown, Share2, DollarSign } from 'lucide-react';
import { usePartnerDetails } from '@/hooks/usePartnerDetails';
import { usePaymentMethods } from '@/hooks/useBilling';
import { BillingOverviewTab } from './billing/BillingOverviewTab';
import { PaymentMethodsTab } from './billing/PaymentMethodsTab';
import BillingHistoryTab from './billing/BillingHistoryTab';
import BoostPacksTab from './billing/BoostPacksTab';
import { AutoRefillTab } from './billing/AutoRefillTab';
import { UsageAnalyticsTab } from './billing/UsageAnalyticsTab';
import { SubscriptionPlansTab } from './billing/SubscriptionPlansTab';
import { ReferralTab } from './billing/ReferralTab';
import { StripeConnectTab } from '@/components/partner/StripeConnectTab';

interface BillingPageProps {
  hideReferralTab?: boolean;
}

export default function BillingPage({ hideReferralTab = false }: BillingPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('overview');
  const { data: paymentMethods } = usePaymentMethods();
  const { data: partnerDetails } = usePartnerDetails();

  const hasPaymentMethod = paymentMethods?.savedMethods && paymentMethods.savedMethods.length > 0;
  const isPartner = partnerDetails !== null;

  // Read tab from URL parameters on load
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const validTabs = ['overview', 'subscription-plans', 'payment-methods', 'analytics', 'history', 'boost-packs', 'auto-refill'];
    if (!hideReferralTab) {
      validTabs.push('referrals');
    }
    if (isPartner) {
      validTabs.push('payouts');
    }
    if (tabParam && validTabs.includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams, hideReferralTab, isPartner]);

  // Update URL when tab changes
  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    setSearchParams({ tab: newTab });
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Billing & Credits</h1>
        <p className="text-muted-foreground">
          Manage your payment methods, track usage, and purchase credit boost packs
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <TabsList className={`grid w-full ${
          isPartner && !hideReferralTab ? 'grid-cols-9' : 
          isPartner && hideReferralTab ? 'grid-cols-8' :
          hideReferralTab ? 'grid-cols-7' : 'grid-cols-8'
        }`}>
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="subscription-plans" className="flex items-center gap-2">
            <Crown className="h-4 w-4" />
            <span className="hidden sm:inline">Plans</span>
          </TabsTrigger>
          <TabsTrigger value="payment-methods" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">Payment</span>
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            <span className="hidden sm:inline">Usage</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              <span className="hidden sm:inline">History</span>
            </TabsTrigger>
          <TabsTrigger value="boost-packs" className="flex items-center gap-2">
            <Zap className="h-4 w-4" />
            <span className="hidden sm:inline">Boost</span>
          </TabsTrigger>
          <TabsTrigger value="auto-refill" className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            <span className="hidden sm:inline">Auto-refill</span>
          </TabsTrigger>
          {isPartner && (
            <TabsTrigger value="payouts" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              <span className="hidden sm:inline">Payouts</span>
            </TabsTrigger>
          )}
          {!hideReferralTab && (
            <TabsTrigger value="referrals" className="flex items-center gap-2">
              <Share2 className="h-4 w-4" />
              <span className="hidden sm:inline">Referrals</span>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <BillingOverviewTab />
        </TabsContent>

        <TabsContent value="subscription-plans" className="space-y-4">
          <SubscriptionPlansTab />
        </TabsContent>

        <TabsContent value="payment-methods" className="space-y-4">
          <PaymentMethodsTab />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <UsageAnalyticsTab />
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
            <BillingHistoryTab />
          </TabsContent>

        <TabsContent value="boost-packs" className="space-y-4">
          <BoostPacksTab hasPaymentMethod={hasPaymentMethod} />
        </TabsContent>

        <TabsContent value="auto-refill" className="space-y-4">
          <AutoRefillTab hasPaymentMethod={hasPaymentMethod} />
        </TabsContent>

        {isPartner && (
          <TabsContent value="payouts" className="space-y-4">
            <StripeConnectTab />
          </TabsContent>
        )}

        {!hideReferralTab && (
          <TabsContent value="referrals" className="space-y-4">
            <ReferralTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
} 